import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  Spin,
  Form,
  Button,
  Tooltip,
  Typography,
  Table,
  Breadcrumb,
  Result,
} from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import get from "lodash/get";
import dayjs from "dayjs";
import { getPartDetailColumns } from "../../constants/tableColumns/summaryReportsColumns/getPartDetailColumns";
import { fetchCompanies } from "../../redux/actions/companyActions";
import { ContainerOutlined, BorderlessTableOutlined } from "@ant-design/icons";
import { fetchPartDetailReport } from "../../redux/actions/reportActions";
import { fetchPartDetailReportPdf } from "../../redux/actions/downloadPdfAction";
import { fetchCompanyParts } from "../../redux/actions/companyActions";
import HeaderSelector from "../../components/reports/partdetailReport/headerSelector";
import requireAuth from "../../hocs/requireAuth";
import ExcelExportButton from "../../components/breakdownSetting/ExcelExportButton";
import { useExcelExport } from "../../hooks/useExcelExport";
import { COMPANY_ADMIN_ROLE_ACCESS } from "../../constants";

const { Title } = Typography;

const PageBreadcrumb = () => {
  return (
    <Breadcrumb
      style={{ fontSize: "14px" }}
      items={[
        {
          title: <BorderlessTableOutlined style={{ fontSize: "16px" }} />,
        },
        {
          title: (
            <>
              <ContainerOutlined style={{ fontSize: "16px" }} />
              <span>Part</span>
            </>
          ),
        },
        {
          title: "Detail Report",
        },
      ]}
    />
  );
};

const initialState = {
  company: undefined,
  templateType: undefined,
  part: undefined,
  date: dayjs(),
};

const PartDetailReport = () => {
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState(null);
  const [partDetails, setPartDetails] = useState(undefined);
  const [showEmpty, setShowEmpty] = useState(false);
  const [filter, setFilters] = useState(initialState);
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { wrapperRef, onDownload, isReady } = useExcelExport(
    [partDetails,getPartDetailColumns],
    `Part-Detail-Report-${get(filter, "date").format("MM-YYYY")}`,
    `Part-Detail-Report`
  );

   const getCompanyParts = (company, query) => {
    setLoading(true);
    dispatch(fetchCompanyParts(get(company, "id"), query))
      .then((parts) => {
        setConfig({
          ...config,
          parts,
        });
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const setinitialCompany = (company) => {
       setFilters({
         ...filter,
         templateType: undefined,
         part:undefined,
         company,
       });
   
       setConfig({
         ...config,
         companies: [company],
         templates: get(company, "template_access_list"),
       });
       form.setFieldsValue({
         template: undefined,
          part: undefined,
         Company: get(company, "id"),
       });
  };

  const getCompanies = () => {
    setLoading(true);
    dispatch(fetchCompanies())
      .then((result) => {
        if (result.length === 1) {
          setinitialCompany(result[0]);
        } else {
          setConfig({ companies: result });
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const getDetails = () => {
    setLoading(true);
    const query = {
      type: get(filter, "templateType"),
      date: get(filter, "date").format("MM-YYYY"),
    };
    dispatch(fetchPartDetailReport(get(filter, "part.id"), query))
      .then((result) => {
        if (get(result, "reports", []).length > 0) {
          setShowEmpty(false);
          setPartDetails(result);
        } else {
          setPartDetails(undefined);
          setShowEmpty(true);
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const onDownloadReport = () => {
    setLoading(true);
    const query = {
      type: get(filter, "templateType"),
      date: get(filter, "date").format("MM-YYYY"),
    };
    dispatch(
      fetchPartDetailReportPdf(get(filter, "part.id"), {
        ...query,
        part_name: get(filter, "part.name"),
      })
    )
      .then((result) => {
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getCompanies();
  }, []);

  return (
    <div className="p-2 h-full flex flex-col">
      <div className="flex flex-row justify-between pb-4">
        <PageBreadcrumb />
      </div>

      <div className="h-full rounded-xl overflow-hidden bg-white flex-1 overflow-y-auto scrollbar-hidden pb-5 min-w-full">
        <Form form={form}>
          <Spin spinning={loading}>
            <HeaderSelector
              form={form}
              filter={filter}
              config={config}
              setConfig={setConfig}
              setFilters={setFilters}
              getCompanyParts={getCompanyParts}
              getDetails={getDetails}
              setPartDetails={setPartDetails}
            />
            {get(partDetails, "reports", []).length > 0 && (
              <div className="w-full px-8 overflow-y-auto">
                <div className="float-right mb-5 space-x-2">
                  <Tooltip className="mb-5" title="Download Report">
                    <Button
                      type="primary"
                      icon={<DownloadOutlined />}
                      onClick={() => onDownloadReport()}
                    />
                  </Tooltip>

                  <Tooltip title="Excel Report">
                    <ExcelExportButton
                      onDownload={onDownload}
                      disabled={!isReady}
                    />
                  </Tooltip>
                </div>
                <div ref={wrapperRef} className="w-full">
                  <div className="w-full table-container">
                    <Title className="mt-10" level={5}>
                      Part Details : {get(filter, "part.name")}
                    </Title>
                    <Table
                      className="mb-20 mt-10 custom-horizontal-scrollbar"
                      dataSource={get(partDetails, "reports", [])}
                      columns={getPartDetailColumns(
                        get(partDetails, "shifts", []),
                        get(filter, "templateType")
                      )}
                      bordered
                      pagination={false}
                     rowKey={(record) => get(record, "id") || crypto.randomUUID()}
                    />
                  </div>
                </div>
              </div>
            )}

            {showEmpty && (
              <div className="px-8 py-8">
                <Result
                  title={`No part exists...for this month!`}
                  subTitle="Please try again with another company, template type or month"
                  status="404"
                />
              </div>
            )}
          </Spin>
        </Form>
      </div>
    </div>
  );
};

export default requireAuth(PartDetailReport, COMPANY_ADMIN_ROLE_ACCESS);
